/**
 * cart-drawer.js
 * CartDrawer — Dawn-style custom element.
 *
 * Listens for:
 *   cart:open            → opens drawer + refreshes cart data
 *   cart:refresh         → refreshes cart data silently (no open)
 *   cart:add { detail: { form? | id, quantity } } → adds item then opens
 *
 * Dispatches:
 *   cart:updated { detail: { cart } }  → after any mutation
 *
 * Cart badge selectors updated: [data-cart-count] anywhere in page.
 */

(function () {
  'use strict';

  /* ── Money formatter ──────────────────────────────────── */
  function money(cents) {
    var val = (cents / 100).toFixed(2);
    return '$' + val.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ════════════════════════════════════════════════════════
     CartDrawer — custom element
  ════════════════════════════════════════════════════════ */
  class CartDrawer extends HTMLElement {

    connectedCallback() {
      this._freeShipping = parseFloat(this.dataset.freeShipping) * 100 || 0;

      /* DOM refs */
      this.overlay    = this.querySelector('[data-cart-overlay]');
      this.panel      = this.querySelector('.cart-drawer__panel');
      this.body       = this.querySelector('[data-cart-body]');
      this.itemsEl    = this.querySelector('[data-cart-items]');
      this.emptyEl    = this.querySelector('[data-cart-empty]');
      this.footerEl   = this.querySelector('[data-cart-footer]');
      this.subtotalEl = this.querySelector('[data-cart-subtotal]');
      this.countEl    = this.querySelector('[data-drawer-count]');
      this.loaderEl   = this.querySelector('[data-cart-loader]');
      this.closeBtn   = this.querySelector('[data-cart-close]');
      this.shippingBar = this.querySelector('[data-shipping-bar]');
      this.shippingFill = this.querySelector('[data-shipping-fill]');
      this.shippingText = this.querySelector('[data-shipping-text]');

      /* Close triggers */
      if (this.overlay) this.overlay.addEventListener('click',   () => this.close());
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());

      /* Escape key */
      this._onKey = (e) => { if (e.key === 'Escape') this.close(); };
      document.addEventListener('keydown', this._onKey);

      /* Delegated item interactions */
      this.addEventListener('click', this._handleItemClick.bind(this));

      /* Global events */
      document.addEventListener('cart:open',    (e) => this.open(e?.detail));
      document.addEventListener('cart:refresh', ()  => this._fetchAndRender(false));
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKey);
    }

    /* ── Open ─────────────────────────────────────────── */
    open(detail) {
      this._previouslyFocused = document.activeElement;
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('cart-drawer-open');
      setTimeout(() => this.panel?.focus(), 50);
      if (detail && detail.cart) {
        this._render(detail.cart);
        this._updateBadges(detail.cart.item_count);
      } else if (window._cartFetchPromise) {
        /* Consume the in-flight fetch started by the ATC handler — it runs
           concurrently with the open animation so items are ready with no
           additional round-trip. */
        var p = window._cartFetchPromise;
        window._cartFetchPromise = null;
        this._setLoading(true);
        p.then((cart) => {
          this._render(cart);
          this._updateBadges(cart.item_count);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        })
        .catch(() => this._fetchAndRender(true))
        .finally(() => this._setLoading(false));
      } else {
        this._fetchAndRender(true);
      }
    }

    /* ── Close ────────────────────────────────────────── */
    close() {
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('cart-drawer-open');
      /* Return focus to the element that opened the drawer */
      if (this._previouslyFocused && typeof this._previouslyFocused.focus === 'function') {
        this._previouslyFocused.focus();
        this._previouslyFocused = null;
      }
    }

    /* ── Fetch /cart.js and re-render ─────────────────── */
    _fetchAndRender(showLoader = true) {
      if (showLoader) this._setLoading(true);
      return fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
        .then(r => r.json())
        .then(cart => {
          this._render(cart);
          this._updateBadges(cart.item_count);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
          return cart;
        })
        .catch(() => {})
        .finally(() => this._setLoading(false));
    }

    /* ── Render cart into drawer ──────────────────────── */
    _render(cart) {
      this._cart = cart; /* latest rendered cart — used by the async message hydrator */
      const hasItems = cart.items.length > 0;

      /* Toggle empty/items/footer */
      if (this.emptyEl)  this.emptyEl.style.display  = hasItems ? 'none' : '';
      if (this.itemsEl)  this.itemsEl.style.display  = hasItems ? ''     : 'none';
      if (this.footerEl) this.footerEl.style.display = hasItems ? ''     : 'none';

      /* Count */
      if (this.countEl) {
        this.countEl.textContent = cart.item_count > 0 ? `(${cart.item_count})` : '';
      }

      /* Subtotal */
      if (this.subtotalEl) this.subtotalEl.textContent = money(cart.total_price);

      /* Items */
      if (this.itemsEl) this.itemsEl.innerHTML = cart.items.map(item => this._itemHtml(item)).join('');

      /* Fill in any shipping messages not covered by the server-rendered map,
         then re-render once (no-op when everything is already cached). */
      this._hydrateMissingMessages(cart);

      /* Free shipping bar */
      this._updateShipping(cart.total_price);
    }

    /* ── Build a single cart item HTML string ─────────── */
    _itemHtml(item) {
      const imgHtml = item.image
        ? `<img src="${item.image.replace(/(\.\w+)$/, '_80x80$1')}" alt="${this._esc(item.title)}" class="cart-item__image" width="80" height="80" loading="lazy">`
        : `<div class="cart-item__image" style="background:#f0f0f0;width:80px;height:80px;border-radius:4px;"></div>`;

      const variantHtml = (item.variant_title && item.variant_title !== 'Default Title')
        ? `<p class="cart-item__variant">${this._esc(item.variant_title)}</p>`
        : '';

      /* Tag-driven shipping message. Server-rendered map (set in cart-drawer.liquid)
         is the source; a cache miss (undefined) is hydrated lazily in _render via
         /products/{handle}.js, so newly added products get their message too. */
      const shipMsg = (window.ShippingTagMessages && window.ShippingTagMessages[item.product_id]) || '';

      return `
        <li class="cart-item" data-cart-item data-item-key="${item.key}">
          <a href="${item.url}" class="cart-item__image-link" tabindex="-1" aria-hidden="true">
            ${imgHtml}
          </a>
          <div class="cart-item__details">
            <a href="${item.url}" class="cart-item__title">${this._esc(item.product_title)}</a>
            ${variantHtml}
            <div class="cart-item__price-row">
              <span class="cart-item__price">${money(item.final_line_price)}</span>
              <div class="cart-item__qty">
                <button class="cart-item__qty-btn" type="button"
                  data-qty-change="-1" data-item-key="${item.key}"
                  aria-label="Decrease quantity" ${item.quantity <= 1 ? 'disabled' : ''}>&minus;</button>
                <span class="cart-item__qty-num" data-item-qty>${item.quantity}</span>
                <button class="cart-item__qty-btn" type="button"
                  data-qty-change="1" data-item-key="${item.key}"
                  aria-label="Increase quantity">&plus;</button>
              </div>
            </div>
            <button class="cart-item__remove" type="button"
              data-remove-item="${item.key}"
              aria-label="Remove ${this._esc(item.product_title)} from cart">Remove</button>
            ${shipMsg}
          </div>
        </li>`;
    }

    /* ── Build shipping message from tags (mirrors shipping-message.liquid) ──
       Only used for the lazy fallback below — case-insensitive, requires both
       "ship-" and "-days", uses the first matching tag, extracts any number. */
    _buildShippingMessage(tags) {
      if (!Array.isArray(tags)) return '';
      for (let i = 0; i < tags.length; i++) {
        const t = String(tags[i]).toLowerCase();
        if (t.indexOf('ship-') !== -1 && t.indexOf('-days') !== -1) {
          const days = t.split('ship-').pop().split('-days')[0];
          if (days) return `<p class="shipping-tag-message">Ships within ${days} days</p>`;
        }
      }
      return '';
    }

    /* ── Lazily resolve messages for products not in the server map ──────────
       e.g. items added via "frequently bought together" during this page view.
       Fetches /products/{handle}.js once per product, caches the result (blank
       included, to avoid refetch loops), then re-renders once. */
    _hydrateMissingMessages(cart) {
      const map = (window.ShippingTagMessages = window.ShippingTagMessages || {});
      const missing = {};
      cart.items.forEach((it) => {
        if (map[it.product_id] === undefined && it.handle) missing[it.product_id] = it.handle;
      });
      const ids = Object.keys(missing);
      if (!ids.length) return;

      /* Respect a localized store root (e.g. "/en-us/") so the lookup never 404s. */
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      Promise.all(ids.map((id) =>
        fetch(root + 'products/' + missing[id] + '.js', { headers: { 'Content-Type': 'application/json' } })
          .then((r) => r.json())
          .then((p) => { map[id] = this._buildShippingMessage(p.tags); })
          .catch(() => { map[id] = ''; })
      )).then(() => this._render(this._cart || cart)); /* re-render the latest cart, not a stale one */
    }

    /* ── Delegated click for qty change + remove ──────── */
    _handleItemClick(e) {
      /* Quantity change */
      const qtyBtn = e.target.closest('[data-qty-change]');
      if (qtyBtn) {
        const key   = qtyBtn.dataset.itemKey;
        const delta = parseInt(qtyBtn.dataset.qtyChange, 10);
        const item  = this.querySelector(`[data-item-key="${key}"]`);
        const numEl = item?.querySelector('[data-item-qty]');
        if (!numEl) return;
        const current  = parseInt(numEl.textContent, 10);
        const newQty   = Math.max(0, current + delta);
        this._updateQty(key, newQty);
        return;
      }

      /* Remove */
      const removeBtn = e.target.closest('[data-remove-item]');
      if (removeBtn) {
        this._updateQty(removeBtn.dataset.removeItem, 0);
        return;
      }
    }

    /* ── Update line item quantity ────────────────────── */
    _updateQty(key, qty) {
      this._setLoading(true);
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      })
      .then(r => r.json())
      .then(cart => {
        this._render(cart);
        this._updateBadges(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      })
      .catch(() => {})
      .finally(() => this._setLoading(false));
    }

    /* ── Free shipping progress ───────────────────────── */
    _updateShipping(totalCents) {
      if (!this._freeShipping || !this.shippingBar) return;
      const pct     = Math.min(100, (totalCents / this._freeShipping) * 100);
      const needed  = money(Math.max(0, this._freeShipping - totalCents));
      const reached = totalCents >= this._freeShipping;

      if (this.shippingFill) this.shippingFill.style.width = pct + '%';
      if (this.shippingText) {
        this.shippingText.textContent = reached
          ? '🎉 You\'ve unlocked free shipping!'
          : `Add ${needed} more to get free shipping`;
      }
    }

    /* ── Update all badge elements on page ────────────── */
    _updateBadges(count) {
      /* FIX: target both the CSS class (.nav__cart-count) used by the header
         section and the data attribute ([data-cart-count]) used by cart-drawer */
      document.querySelectorAll('.nav__cart-count, [data-cart-count]').forEach(el => {
        el.textContent   = count > 0 ? count : '';
        el.style.display = count > 0 ? '' : 'none';
      });
    }

    /* ── Loading state ────────────────────────────────── */
    _setLoading(on) {
      if (!this.loaderEl) return;
      if (on) { this.loaderEl.removeAttribute('hidden'); }
      else    { this.loaderEl.setAttribute('hidden', ''); }
    }

    /* ── HTML escape ──────────────────────────────────── */
    _esc(str) {
      return String(str || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    }
  }

  /* ── Register custom element ──────────────────────────── */
  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', CartDrawer);
  }

  /* ════════════════════════════════════════════════════════
     CartLink — intercepts /cart links when pure drawer mode
     is active. In 'hybrid' and 'page' modes, cart-icon clicks
     fall through to the native /cart navigation (that is the
     whole point of hybrid — icon goes to page, ATC opens drawer).
  ════════════════════════════════════════════════════════ */
  class CartLink {
    static init() {
      const mode = document.documentElement.dataset.cartType
                || document.body.dataset.cartType
                || 'page';
      if (mode !== 'drawer') return;

      document.addEventListener('click', (e) => {
        const link = e.target.closest('a[href="/cart"]');
        if (!link) return;
        /* Let links inside the cart drawer navigate normally (e.g. "View full cart") */
        if (link.closest('cart-drawer')) return;
        e.preventDefault();
        document.dispatchEvent(new CustomEvent('cart:open'));
      });
    }
  }

  /* ════════════════════════════════════════════════════════
     CartBadgeSync — listens to cart:updated and cart:refresh
     to keep header badge counts fresh without page reload.
  ════════════════════════════════════════════════════════ */
  class CartBadgeSync {
    static init() {
      /* On cart:refresh, fetch cart and update badges.
         Targets both .nav__cart-count (header CSS class) and
         [data-cart-count] (data attribute) to cover all badge elements. */
      document.addEventListener('cart:refresh', () => {
        fetch('/cart.js')
          .then(r => r.json())
          .then(cart => {
            document.querySelectorAll('.nav__cart-count, [data-cart-count]').forEach(el => {
              el.textContent   = cart.item_count > 0 ? cart.item_count : '';
              el.style.display = cart.item_count > 0 ? '' : 'none';
            });
          })
          .catch(() => {});
      });
    }
  }

  /* ── Boot ─────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  function boot() {
    CartLink.init();
    CartBadgeSync.init();
  }

})();
